import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals'
import { sendFromHiddenPage } from './sendFromHiddenPage'

/** How a Web Vitals metric reaches us — the `web-vitals` `onX` callbacks. */
export type WebVitalsSource = (report: (metric: Metric) => void) => void

const SOURCES: readonly WebVitalsSource[] = [onLCP, onCLS, onINP, onFCP, onTTFB]

/**
 * `web_vitals_reported` — one event per Core Web Vitals metric of a page
 * load (TASK-1173): `metric` (LCP | CLS | INP | FCP | TTFB), `value` (ms; CLS
 * unitless), `rating` (good | needs-improvement | poor, Google's
 * thresholds), `navigation_type`, `page_path`. Web-only, not part of the
 * cross-platform contract. Amplitude has no Web Vitals in SDK 2.11.1, and
 * the free plan has no performance product — this is the replacement.
 *
 * Each metric is reported once, with its final value (`web-vitals` default
 * `reportAllChanges: false`). CLS and INP are final only when the page is
 * hidden, so those emits go through `sendFromHiddenPage`.
 */
export function startWebVitalsReporting(
  report: (event: string, params: Record<string, unknown>) => void,
  sources: readonly WebVitalsSource[] = SOURCES,
): void {
  const emit = (metric: Metric) => {
    const send = () =>
      report('web_vitals_reported', {
        metric: metric.name,
        value:
          metric.name === 'CLS' ? Math.round(metric.value * 1000) / 1000 : Math.round(metric.value),
        rating: metric.rating,
        navigation_type: metric.navigationType,
        page_path: window.location.pathname,
      })
    if (document.visibilityState === 'hidden') sendFromHiddenPage(send)
    else send()
  }
  for (const source of sources) {
    try {
      source(emit)
    } catch {
      // A browser without the needed PerformanceObserver type — skip the metric.
    }
  }
}
